import {CallModel} from '../models/call.model';
import {CallState} from '../models/enums/call-state.enum';
import {
  AnsweredCall,
  CallRetrieved,
  CallToggled,
  CreateCall,
  Dequeue,
  DisconnectedCall,
  Enqueue,
  EUAnsweredCall,
  EUCreateCall,
  EUDisconnectedCall,
  EUNoConnectCall,
  NoConnectCall,
  TransferCallAnswered,
  TransferCallEnd,
  TransferCallEvent
} from './push-event/push-events';
import {TransferState} from '../models/enums/transfer-state.enum';

const byCreationTime = (a: CallModel, b: CallModel) => a?.creationTime?.getTime() - b?.creationTime?.getTime();

export class CallRegistry {
  constructor(private agentLogin: string, private agentNumber: string) {}

  calls: CallModel[] = [];

  setActiveCallData(calls: CallModel[]) {
    this.calls = calls;
  }

  getCurrentCall(): CallModel {
    return this.calls.find(
      call =>
        !call.isPrivateCall &&
        (call.agentLogin === this.agentLogin || call.answeringNumber === this.agentNumber) &&
        (call.state === CallState.Calling || call.state === CallState.Connected || call.state === CallState.TryingAgent)
    );
  }

  getCurrentPrivateCall(): CallModel {
    return this.calls.find(call => call.triggerPoint > 0 && (call.state === CallState.Calling || call.state === CallState.Connected));
  }

  getOnHoldCalls(): CallModel[] {
    return this.calls.filter(call => call.state === CallState.OnHold).sort(byCreationTime);
  }

  handleCreateCall(event: CreateCall): CallModel {
    const incomingCall = CallModel.fromCreateCallEvent(event);
    const call = this.calls.find(c => c.sessionId === event.session_id);
    if (call) {
      call.state = CallState.TryingAgent;
      call.agentLogin = event.agent_login;
      call.interactionDisabled = false;
      return call;
    } else {
      this.calls.push(incomingCall);
    }
    this.log();
    return incomingCall;
  }

  handleEUCreateCall(event: EUCreateCall): CallModel {
    const incomingCall = CallModel.fromEUCreateCall(event);
    this.calls.push(incomingCall);
    this.log();
    return incomingCall;
  }

  handleDisconnectedCall(event: DisconnectedCall) {
    this.removeActiveCall(event.session_id);
    this.log();
  }

  handleNoConnectCall(event: NoConnectCall) {
    const call = this.calls.find(call => call.sessionId === event.session_id);
    if (call) {
      call.agentLogin = null;
    }
    if (call && call.state === CallState.TryingAgent) {
      // call is in queue, but got no answer from agent, wait for Dequeue event.
      call.state = CallState.OnHold;
    } else {
      this.removeActiveCall(event.session_id);
    }
    this.log();
  }

  handleTransferCall(event: TransferCallEvent) {
    // const outgoingCall = CallModel.fromTransferCallEvent(event);
    // this.calls.push(outgoingCall);
    const incomingCall = this.getCurrentCall();
    if (incomingCall && incomingCall.sessionId === event.session_id) {
      if (event.transfer_type === 0) {
        // Blind transfer
        this.removeActiveCall(incomingCall.sessionId);
      } else {
        incomingCall.creationTime = new Date();
        incomingCall.answeringNumber = event.transferToNumber;
        incomingCall.transferState = TransferState.Ringing;
      }
    } else {
      console.error('No incoming call when receiving handleTransferCall');
    }
    this.log();
  }

  handleTransferCallEnd(event: TransferCallEnd) {
    const incomingCall = this.getCurrentCall();

    if (incomingCall && incomingCall.sessionId === event.session_id) {
      if (incomingCall.transferState === TransferState.ConsultingA) {
        // TransferState 8

        if (event.transferToNumber === event.a_number) {
          // A hung up
          incomingCall.transferState = TransferState.NoA_CHold;
        } else {
          incomingCall.transferState = TransferState.Idle;
        }
      } else if (incomingCall.transferState === TransferState.ConsultingC) {
        // TransferState 5
        if (event.transferToNumber === event.a_number) {
          // A hung up
          incomingCall.transferState = TransferState.NoA_ConsultingC;
        } else {
          incomingCall.transferState = TransferState.NoC_AHold;
        }
      } else if (incomingCall.transferState === TransferState.Ringing) {
        // TransferState 1
        // If C does not respond, if A hangs up in transferState 1 it will be a "DisconnectCall".
        incomingCall.transferState = TransferState.NoC_AHold;
      }

      // if (incomingCall.answeringNumber === event.transferToNumber) {
      //   incomingCall.transferState = TransferState.NoC_AHold;
      // } else {
      //   incomingCall.transferState = TransferState.NoA_ConsultingC;
      // }
    }

    // const outgoingCall = this.getOutgoingCall();
    // if (outgoingCall && outgoingCall.answeringNumber === event.transferToNumber && outgoingCall.sessionId === event.session_id) {
    //   this.removeCall(event.session_id, 'C');
    // }
  }

  handleAnsweredCall(event: AnsweredCall) {
    const incomingCall = this.getCurrentCall();
    if (incomingCall && incomingCall.sessionId === event.session_id) {
      incomingCall.creationTime = new Date();
      incomingCall.state = CallState.Connected;
      incomingCall.transferState = TransferState.Idle;
    } else {
      // this is not optimal atm as a_number is not available in this event.
      this.calls.push(CallModel.fromAnsweredCallEvent(event));
    }
    this.log();
  }

  handleEUAnsweredCall(event: EUAnsweredCall) {
    const incomingCall = this.getCurrentPrivateCall();
    if (incomingCall && incomingCall.sessionId === event.session_id) {
      incomingCall.creationTime = new Date();
      incomingCall.state = CallState.Connected;
    } else {
      this.calls.push(CallModel.fromEUAnsweredCall(event));
    }
    this.log();
  }

  handleEUDisconnectCall(event: EUDisconnectedCall) {
    this.removePrivateCall(event.session_id);
    this.log();
  }

  handleEUNoConnectCall(event: EUNoConnectCall) {
    this.removePrivateCall(event.session_id);
    this.log();
  }

  handleTransferCallAnswered(event: TransferCallAnswered) {
    const incomingCall = this.getCurrentCall();
    if (incomingCall && incomingCall.sessionId === event.session_id) {
      incomingCall.creationTime = new Date();
      incomingCall.transferState = TransferState.ConsultingC;
    } else {
      console.error('No incoming call when receiving handleTransferCall');
    }
    this.log();
  }

  removeActiveCall(sessionId: number) {
    this.calls = this.calls.filter(call => {
      return !(call.sessionId === sessionId && call.state !== CallState.OnHold);
    });
  }

  removePrivateCall(sessionId: number) {
    this.calls = this.calls.filter(call => {
      return !(call.sessionId === sessionId && call.isPrivateCall);
    });
  }

  handleRetrieveCall(event: CallRetrieved) {
    const incomingCall = this.getCurrentCall();
    if (incomingCall && incomingCall.sessionId === event.session_id) {
      if (incomingCall.transferState === TransferState.NoC_AHold) {
        incomingCall.transferState = TransferState.Idle;
      } else if (incomingCall.transferState === TransferState.NoA_CHold) {
        incomingCall.transferState = TransferState.NoA_ConsultingC;
      }
    }
  }

  isInCall(): boolean {
    const currentCall = this.getCurrentCall();
    return currentCall && currentCall.state === CallState.Connected;
  }

  handleEnqueue(event: Enqueue): CallModel {
    const incomingCall = CallModel.fromEnqueueEvent(event);
    const call = this.calls.find(c => c.sessionId === event.session_id);
    if (call) {
      call.state = CallState.OnHold;
      this.log();
      return call;
    } else {
      this.calls.push(incomingCall);
      this.log();
      return incomingCall;
    }
  }

  handleDequeue(event: Dequeue) {
    // Remove when BNO available in dequeue event from backend
    if (!event.bno) {
      const callInfo = this.calls.find(call => call.sessionId === event.session_id);
      event.bno = callInfo.serviceNumber;
      console.warn('BNO not available in dequeue event. Monkey patching...');
    }

    this.calls = this.calls.filter(call => {
      return !(call.sessionId === event.session_id && call.state === CallState.OnHold);
    });
    this.log();
  }

  handleCallToggled(event: CallToggled) {
    const leg = event.leg2;
    const incomingCall = this.getCurrentCall();

    if (incomingCall && incomingCall.sessionId === event.session_id && event.status === 0) {
      if (leg === 'A') {
        incomingCall.transferState = TransferState.ConsultingA;
      } else if (leg === 'C') {
        incomingCall.transferState = TransferState.ConsultingC;
      }
    }
    this.log();
  }

  getCallsInQueue(sno: string, cid: number): CallModel[] {
    return this.calls
      .filter(
        call =>
          (call.state === CallState.OnHold || call.state === CallState.TryingAgent) &&
          call.serviceNumber === sno &&
          call.cid === cid &&
          !call.isPrivateCall
      )
      .sort(byCreationTime);
  }

  log() {
    console.log('CallRegistry calls');
    console.log(
      JSON.stringify(
        this.calls.map(c => c.call),
        null,
        '2'
      )
    );
  }
}
